import { ColumnDef, Row } from "@tanstack/react-table";
import { formatSourceName } from "@/features/global/components/NotesTable/noteHelpers";
import { SortFieldType } from "./sortUtils";

interface BuildSourceColumnProps<T> {
  accessorKey: string;
  header: string;
  getParentLink: (row: T) => string | null | undefined;
  getParentType?: (row: T) => string | null | undefined;
  getText?: (row: T) => string | null | undefined;
  accessorFn?: (row: T) => unknown;
  size?: number;
  minSize?: number;
  sortType?: SortFieldType;
  enableSorting?: boolean;
  meta?: Record<string, any>;
  navigate?: (to: string) => void;
  currentPathname?: string;
}

/**
 * Helper to create a column that displays a clickable source link.
 * The link navigates to the parent record, and clicking it stops event propagation
 * so the row click handler doesn't fire.
 *
 * Pass `getText` to show an entity name (section, facility) instead of a formatted parent type.
 * Empty labels render "--". A label with no `parentLink` (or no `navigate`) stays plain text.
 * Linked labels are underlined. Do not add a trailing chevron — expand chevrons stay on expandable rows.
 *
 * @example
 * buildSourceColumn({
 *   accessorKey: "source",
 *   header: "Source",
 *   getParentLink: (note) => note.parentId ? `/people/${note.pgaId}/membership` : null,
 *   getParentType: (note) => note.parentType,
 *   size: 150
 * })
 *
 * @example
 * buildSourceColumn({
 *   accessorKey: "section",
 *   header: "Section",
 *   getText: (person) => person.primarySection?.name,
 *   getParentLink: (person) => person.primarySection?.id ? `/sections/${person.primarySection.id}` : null,
 *   navigate
 * })
 */
export function buildSourceColumn<T>({
  accessorKey,
  header,
  getParentLink,
  getParentType,
  getText,
  accessorFn,
  size,
  minSize,
  sortType,
  enableSorting,
  meta,
  navigate,
  currentPathname
}: BuildSourceColumnProps<T>): ColumnDef<T> {
  const CellComponent = ({ row }: { row: Row<T> }) => {
    const parentLink = getParentLink(row.original);
    const parentType = getParentType?.(row.original);
    const text = getText?.(row.original) || (parentType ? formatSourceName(parentType) : "");

    if (!text) {
      return <span className="opacity-20">--</span>;
    }

    const linkPathname = parentLink?.split("?")[0];
    const isCurrentPage = linkPathname && currentPathname === linkPathname;

    if (!parentLink || isCurrentPage || !navigate) {
      return <span>{text}</span>;
    }

    const stopRowNavigation = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
    };

    const openParent = (e: React.MouseEvent) => {
      stopRowNavigation(e);
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
        window.open(parentLink, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(parentLink);
    };

    return (
      <div
        data-source-link="true"
        onClick={openParent}
        onAuxClick={openParent}
        onMouseDown={stopRowNavigation}
        className="inline-flex items-center"
      >
        <button
          type="button"
          onClick={openParent}
          onAuxClick={openParent}
          onMouseDown={stopRowNavigation}
          className="p-0 m-0 bg-transparent border-0 text-left underline text-sys-brand-on-surface hover:text-sys-brand-on-surface-variant cursor-pointer"
        >
          {text}
        </button>
      </div>
    );
  };

  return {
    ...(accessorFn ? { id: accessorKey, accessorFn } : { accessorKey }),
    header,
    ...(size && { size }),
    ...(minSize && { minSize }),
    ...(enableSorting !== undefined && { enableSorting }),
    meta: {
      ...(sortType && { sortType }),
      ...meta
    },
    cell: CellComponent
  };
}
